import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useMemo, useState } from 'react';
import { MenuTrigger, SubmenuTrigger } from 'react-aria-components';

import { Button, Menu, MenuItem, Popover, Text } from '@/components/core';
import { ChevronIcon, CSVIcon, DeleteIcon, DownloadIcon, JSONIcon, OverflowIcon, RerunIcon, ReviewIcon, RotateIcon } from '@/components/icons';
import { useCreateCSVExport, useCreateJSONExport, useDownloadFile } from '@/hooks';
import { toAgentRunId, useGetAgentRun } from '@/hooks/api';

import { DeleteAgentRunsModal } from './DeleteAgentRunsModal';
import { useRerunCallbacks } from './rerun';
import { RerunAgentRunsModal } from './RerunAgentRunsModal';

export type RunOverflowMenuProps = {
  agentId: string;
  runId: string;
};

const IRunOverflowMenu = ({ agentId, runId }: RunOverflowMenuProps) => {
  const navigate = useNavigate();

  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId));

  const goToRun = useCallback(async () => {
    if (agentId) {
      await navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: { agentId: agentId, runId },
        search: { showPanel: 'agent-runs' },
      });
    }
  }, [navigate, agentId, runId]);

  const runIds = useMemo(() => [runId], [runId]);

  const { onRerunFromStart, onRerunFromExport, progress, isPending } = useRerunCallbacks({
    agentId,
    selectedRunIds: new Set(runIds),
  });

  const { create: createCSV } = useCreateCSVExport({ agentId, runIds });
  const { create: createJSON } = useCreateJSONExport({ agentId, runIds });
  const { download: downloadCSV } = useDownloadFile({ fetchData: createCSV, downloadedFileName: 'export.csv' });
  const { download: downloadJSON } = useDownloadFile({ fetchData: createJSON, downloadedFileName: 'export.json' });

  const [deleteRun, setDeleteRun] = useState(false);

  return (
    <>
      <RerunAgentRunsModal progress={progress} isOpen={isPending} />
      <DeleteAgentRunsModal isOpen={deleteRun} agentId={agentId} runIds={runIds} onClose={() => setDeleteRun(false)} />
      <MenuTrigger>
        <Button variant="plain" className="!p-1 focus:bg-transparent">
          <OverflowIcon className="invisible group-hover/row:visible group-data-[pressed]/button:visible" />
        </Button>
        <Popover className="shadow-md">
          <Menu>
            <MenuItem onAction={goToRun}>
              <ReviewIcon />
              <Text>{run?.status === 'ready-for-review' ? 'Review' : 'View details'}</Text>
            </MenuItem>

            <SubmenuTrigger>
              <MenuItem>
                <RerunIcon />
                <Text>Rerun</Text>
                <div className="grow" />
                <ChevronIcon rotation="right" />
              </MenuItem>

              <Popover className="shadow-md" offset={-4}>
                <Menu>
                  <MenuItem onAction={onRerunFromStart}>
                    <Text>Rerun from start</Text>
                  </MenuItem>

                  <MenuItem onAction={onRerunFromExport}>
                    <Text>Rerun export</Text>
                  </MenuItem>
                </Menu>
              </Popover>
            </SubmenuTrigger>

            <SubmenuTrigger>
              <MenuItem>
                <DownloadIcon />
                <Text>Export</Text>
                <div className="grow" />
                <ChevronIcon rotation="right" />
              </MenuItem>

              <Popover className="shadow-md" offset={-4}>
                <Menu>
                  <MenuItem onAction={downloadCSV}>
                    <CSVIcon />
                    <Text>Export as CSV</Text>
                  </MenuItem>

                  <MenuItem onAction={downloadJSON}>
                    <JSONIcon />
                    <Text>Export as JSON</Text>
                  </MenuItem>
                </Menu>
              </Popover>
            </SubmenuTrigger>

            <MenuItem variant="error" onAction={() => setDeleteRun(true)}>
              <DeleteIcon />
              <Text>Delete</Text>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>
    </>
  );
};

export const RunOverflowMenu = memo(IRunOverflowMenu) as typeof IRunOverflowMenu;
